import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { toast } from "sonner";
import { useLocalValue } from "@/hooks/use-local-value";
import { track } from "@/lib/analytics";
import { redirect } from "@/lib/browser";
import {
  claimLicense,
  forgetLicense,
  LICENSE_KEY,
  needsRecheck,
  parseLicense,
  startCheckout,
  storeLicense,
  verifyLicense,
} from "@/lib/license";

const RETURN_KEY = "qpp_license_return";

/** The license stored in this browser, or null. Ads are hidden whenever there is one. */
export function useLicense() {
  return parseLicense(useLocalValue(LICENSE_KEY));
}

/**
 * Asks PocketBase about the stored key once a day. Ads stay hidden meanwhile (no flash for
 * paying users); a key that is no longer valid (refunded) is dropped and the ads come back.
 * Network trouble keeps the key. Mounted once, in App.
 */
export function useLicenseRecheck() {
  const license = useLicense();
  const key = license && needsRecheck(license) ? license.key : null;
  useEffect(() => {
    if (!key) return;
    verifyLicense(key)
      .then((valid) => {
        if (valid) storeLicense(key);
        else {
          forgetLicense();
          toast.info("Your license is no longer valid, so ads are back.");
        }
      })
      .catch(() => undefined);
  }, [key]);
}

/** Sends the buyer to the checkout, remembering where to come back to. */
export function useBuyLicense() {
  return useMutation({
    mutationFn: startCheckout,
    onSuccess: (url) => {
      track("license_checkout_started");
      try {
        sessionStorage.setItem(RETURN_KEY, window.location.pathname);
      } catch {
        // Comes back to the home page instead.
      }
      redirect(url);
    },
    onError: (error: Error) => toast.error(error.message || "Could not start the checkout"),
  });
}

/** Where the buyer was before the checkout. */
export function licenseReturnPath(): string {
  try {
    return sessionStorage.getItem(RETURN_KEY) || "/";
  } catch {
    return "/";
  }
}

export class InvalidLicenseError extends Error {
  constructor() {
    super("This key is not valid. Check it against your receipt email.");
  }
}

export function useActivateLicense() {
  return useMutation({
    mutationFn: async (key: string) => {
      if (!(await verifyLicense(key))) throw new InvalidLicenseError();
      return key;
    },
    onSuccess: (key) => {
      storeLicense(key);
      track("license_activated");
    },
  });
}

/**
 * Claims the key of a finished checkout. Polar grants the key a few seconds after the payment,
 * so this keeps asking for up to a minute.
 */
export function useClaimLicense(checkoutId: string) {
  return useQuery({
    queryKey: ["license", "claim", checkoutId],
    queryFn: () => claimLicense(checkoutId),
    enabled: Boolean(checkoutId),
    staleTime: Infinity,
    refetchInterval: (query) =>
      query.state.data === null && query.state.dataUpdateCount < 30 ? 2000 : false,
  });
}
